// src/Components/Common/Navbar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoDark from '../../Assets/Image/esiproN.png';
import logoWhite from '../../Assets/Image/esiproB.png';
import logoMenu from '../../Assets/Image/esipro.png';
import { Menu } from 'lucide-react';
import { useLang } from './LanguageContext';
import { useTestTransition } from '../../Pages/Test/TestTransitionContext';

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);
    return isMobile;
}

function Navbar() {
    const { lang, toggleLang } = useLang();
    const isEnglish = lang === 'en';
    const location = useLocation();
    const navigate = useNavigate();
    const { phase, openMenu, triggerMenuSvg } = useTestTransition();
    const isMobile = useIsMobile();

    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const isVideoIntro = location.pathname === '/';
    const isHomePage = location.pathname === '/home';
    const isTransparentNav = isVideoIntro;
    const logo = isTransparentNav ? logoMenu : logoDark;
    const accentColor = isTransparentNav ? '#fff' : 'rgb(152,25,29)';

    const isTestArea = location.pathname.startsWith('/test');
    const isTestMenu = location.pathname === '/test';

    const handleLogoClick = () => {
        if (isVideoIntro) {
            navigate('/home'); // su `/` → apri menu
        } else {
            navigate('/'); // da qualsiasi altra pagina → vai al video
        }
    };

    const handleMenuClick = () => {
        if (isTestArea && !isTestMenu) {
            triggerMenuSvg();
        } else if (isTestArea) {
            // do nothing
        } else if (phase === 'menu' || phase === 'openFromHeader' || phase === 'morphToHeader') {
            // overlay already owns the interaction
        } else if (isHomePage) {
            openMenu();
        } else {
            triggerMenuSvg();
        }
    };

    return (
        <nav
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: isMobile
                    ? '12px 16px'
                    : scrolled && !isTransparentNav ? '10px 30px' : '20px 30px',
                minHeight: isMobile ? '60px' : scrolled && !isTransparentNav ? '60px' : '95px',
                borderBottom: 'none',
                backgroundColor: isTransparentNav ? 'transparent' : '#fff',
                position: isTransparentNav ? 'absolute' : 'sticky',
                top: 0,
                left: 0,
                right: 0,
                zIndex: isTransparentNav ? 10000 : 1000,
                transition: 'padding 0.3s ease, min-height 0.3s ease, box-shadow 0.3s ease',
                boxShadow: scrolled && !isTransparentNav ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
            }}
        >
            <button
                onClick={handleLogoClick}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', alignSelf: 'center', lineHeight: 0, padding: 0 }}
            >
                <img
                    src={logo}
                    alt="Logo"
                    style={{
                        height: isMobile ? '22px' : scrolled && !isTransparentNav ? '22px' : '30px',
                        display: 'block',
                        verticalAlign: 'middle',
                        transition: 'height 0.3s ease',
                    }}
                />
            </button>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'center',
                    gap: isMobile ? '10px' : '16px',
                }}
            >
                {/* Toggle lingua */}
                <button
                    onClick={toggleLang}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '13px' : scrolled && !isTransparentNav ? '13px' : '16px',
                        fontWeight: '600',
                        color: accentColor,
                        padding: 0,
                        position: 'relative',
                        overflow: 'hidden',
                        width: isMobile ? '22px' : scrolled && !isTransparentNav ? '22px' : '30px',
                        height: isMobile ? '22px' : scrolled && !isTransparentNav ? '22px' : '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <span
                        style={{
                            display: 'inline-block',
                            transition: 'transform 0.3s ease, opacity 0.3s ease',
                            transform: isEnglish ? 'translateY(-100%)' : 'translateY(0)',
                            opacity: isEnglish ? 0 : 1,
                        }}
                    >
                        IT
                    </span>
                    <span
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.3s ease, opacity 0.3s ease',
                            transform: isEnglish ? 'translateY(0)' : 'translateY(100%)',
                            opacity: isEnglish ? 1 : 0,
                        }}
                    >
                        EN
                    </span>
                </button>

                {/* Bottone menu */}
                <button
                    className="menu-button"
                    onClick={handleMenuClick}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: isMobile ? '22px' : scrolled && !isTransparentNav ? '22px' : '30px',
                        height: isMobile ? '22px' : scrolled && !isTransparentNav ? '22px' : '30px',
                    }}
                >
                    <Menu
                        size={isMobile ? 22 : scrolled && !isTransparentNav ? 22 : 30}
                        color={accentColor}
                        style={{ flexShrink: 0 }}
                    />
                </button>
            </div>
        </nav>
    );
}

export default Navbar;
